import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import './ComponentActionsDropdown.css';

export interface ActionsMenuItem {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    danger?: boolean;
    disabled?: boolean;
    /** Tooltip, e.g. why the item is disabled. */
    title?: string;
}

interface ActionsMenuProps {
    items: ActionsMenuItem[];
    /** Disables the three-dot button itself (e.g. while saving). */
    disabled?: boolean;
}

/** Three-dot menu for rarely used actions. Same look as the component pages' actions menu; renders nothing without items. */
const ActionsMenu: React.FC<ActionsMenuProps> = ({ items, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const outside = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
        document.addEventListener('mousedown', outside);
        document.addEventListener('keydown', escape, true);
        return () => {
            document.removeEventListener('mousedown', outside);
            document.removeEventListener('keydown', escape, true);
        };
    }, [open]);

    if (items.length === 0) return null;

    return (
        <div ref={ref} className="actions-dropdown">
            <button type="button" className={`actions-dropdown-button ${open ? 'actions-dropdown-button--active' : ''}`}
                onClick={() => setOpen(o => !o)} disabled={disabled}
                title="Fler åtgärder" aria-label="Fler åtgärder" aria-haspopup="menu" aria-expanded={open}>
                <MoreVertical size={18} color="#666" />
            </button>
            {open && (
                <div className="actions-dropdown-menu" role="menu">
                    {items.map(item => (
                        <button key={item.label} type="button" role="menuitem"
                            className={`actions-dropdown-item ${item.danger ? 'actions-dropdown-item--danger' : ''}`}
                            disabled={item.disabled} title={item.title}
                            onClick={() => { setOpen(false); item.onClick(); }}>
                            {item.icon}<span>{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ActionsMenu;
